// The call controls' glyphs, in the app's one icon family (1.6-stroke outlines on a 24 grid —
// design/icons.tsx), drawn for the SF Symbols the Mac huddle uses: headphones, mic / mic.slash,
// video / video.slash, rectangle.inset.filled.and.person.filled (share), face.smiling (react),
// hand.raised, arrow.up.left.and.arrow.down.right (expand), phone.down.fill (leave).
import type { CSSProperties, JSX } from 'react';

const paths = {
  headphones: (
    <>
      <path d="M4 15v-3a8 8 0 0 1 16 0v3" />
      <rect x="3.5" y="14" width="4.5" height="6.5" rx="1.8" />
      <rect x="16" y="14" width="4.5" height="6.5" rx="1.8" />
    </>
  ),
  mic: (
    <>
      <rect x="9" y="3" width="6" height="11.5" rx="3" />
      <path d="M5.5 11.5a6.5 6.5 0 0 0 13 0M12 18v3" />
    </>
  ),
  micSlash: (
    <>
      <path d="M15 9.5V6a3 3 0 0 0-5.6-1.5M9 9v2.5a3 3 0 0 0 4.6 2.5" />
      <path d="M5.5 11.5a6.5 6.5 0 0 0 10.6 5M18.5 11.5a6.4 6.4 0 0 1-.5 2.5M12 18v3M3.5 3.5l17 17" />
    </>
  ),
  video: (
    <>
      <rect x="3" y="6.5" width="12.5" height="11" rx="2.5" />
      <path d="m15.5 10.5 5.5-3v9l-5.5-3" />
    </>
  ),
  videoSlash: (
    <>
      <path d="M8 6.5h5a2.5 2.5 0 0 1 2.5 2.5v4.5M15.5 16v-.5A2.5 2.5 0 0 1 13 17.5H5.5A2.5 2.5 0 0 1 3 15V9a2.5 2.5 0 0 1 2-2.4" />
      <path d="m15.5 10.5 5.5-3v9l-3-1.6M3 3l18 18" />
    </>
  ),
  screen: (
    <>
      <rect x="2.5" y="4" width="19" height="13" rx="2.5" />
      <path d="M12 13.5v-6M9.2 10 12 7.2l2.8 2.8M8 20.5h8" />
    </>
  ),
  screenStop: (
    <>
      <rect x="2.5" y="4" width="19" height="13" rx="2.5" />
      <path d="m9.5 8 5 5M14.5 8l-5 5M8 20.5h8" />
    </>
  ),
  smile: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M8.5 14.5a4.5 4.5 0 0 0 7 0M9 9.5h.01M15 9.5h.01" />
    </>
  ),
  hand: <path d="M8.5 12.5V5.8a1.4 1.4 0 0 1 2.8 0V11m0-6.6V3.9a1.4 1.4 0 0 1 2.8 0V11m0-5.8a1.4 1.4 0 0 1 2.8 0V12m0-3.6a1.4 1.4 0 0 1 2.8 0v6.1A6.5 6.5 0 0 1 13.2 21h-1a6 6 0 0 1-4.8-2.4L4.3 14.4a1.5 1.5 0 0 1 2.3-1.9l1.9 2" />,
  expand: <path d="M14 4h6v6M20 4l-6.5 6.5M10 20H4v-6M4 20l6.5-6.5" />,
  collapse: <path d="M20 4l-6 6M14 5v5h5M4 20l6-6M10 19v-5H5" />,
  phoneDown: <path d="M2.8 13.6c5.3-4.4 13.1-4.4 18.4 0l-1.5 2.9-3.6-1.1-.4-2.6a10.5 10.5 0 0 0-7.4 0l-.4 2.6-3.6 1.1Z" />,
  people: (
    <>
      <circle cx="9" cy="8.5" r="3.2" />
      <path d="M3 19.5a6 6 0 0 1 12 0M15.5 5.6a3.2 3.2 0 0 1 0 6M17.5 14a5.8 5.8 0 0 1 3.5 5.5" />
    </>
  ),
  chevronUp: <path d="m6 15 6-6 6 6" />,
  check: <path d="m5 12.5 4.5 4.5L19 7.5" />,
  x: <path d="M6 6l12 12M18 6 6 18" />,
} satisfies Record<string, JSX.Element>;

export type CallIconName = keyof typeof paths;

export function CallIcon({ name, size = 16, strokeWidth = 1.7, style, title, filled }: { name: CallIconName; size?: number; strokeWidth?: number; style?: CSSProperties; title?: string; filled?: boolean }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill={filled ? 'currentColor' : 'none'}
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden={title ? undefined : true}
      role={title ? 'img' : undefined}
      style={{ flex: 'none', ...style }}
    >
      {title ? <title>{title}</title> : null}
      {paths[name]}
    </svg>
  );
}
